import { useEffect, useState } from 'react';
import { useQuery } from 'react-query';

import { EyeIcon, PaymentLinksIcon } from '../../../../assets/svg';
import { EditIcon } from '../../../../assets/svg/EditIcon';
import { CustomShareIcon } from '../../../../assets/svg/payment-links-custom/CustomShareIcon';
import { RecurrentPayIcon } from '../../../../assets/svg/payment-links-types/RecurrentPay';
import { UniquePayIcon } from '../../../../assets/svg/payment-links-types/UniquePay';
import { Breadcrumb } from '../../../../components/breadcrumb';
import { DateAndTime } from '../../../../components/date-and-time';
import { Select } from '../../../../components/form/select';
import { ETPayFilters } from '../../../../components/table/components/filters';
import { Tooltip } from '../../../../components/tooltip';
import { ViewWrapper } from '../../../../components/view-wrapper';
import { CONSTANTS } from '../../../../config/constants';
import { useMerchant } from '../../../../hooks/useMerchant';
import { Payment } from '../../../../models/common-payments';
import { CustomLink, CustomLinkSubStatus } from '../../../../models/payment-links-custom';
import { ColumnInterface, Filters } from '../../../../models/table';
import { PaymentLinksCustomServices } from '../../../../services/payment-links-custom';
import { validatePermission } from '../../../../utils/common';
import { defaultPagination } from '../../../../utils/defaults';
import { EditLinkPL } from '../../components/edit-link';
import { CustomLinkComponentStatus } from '../../components/link-status';
import { CustomPaymentLinksTable } from '../../components/links-table';
import { ContainerActionsLinksComponent } from '../../components/links-table/ContainerActionsComponent';
import { ModalLink } from '../../components/modal-link';
import { ModalPayment } from '../../components/modal-payment';
import { NewLinkPL } from '../../components/new-link';
import { PauseLinkModal } from '../../components/pause-link';
import { ShareLinkModal } from '../../components/share-link-modal';
import { StatusContainer } from '../../components/status/styles';
import { AmountContainer } from '../../components/table/styles';
import { TEXTS } from '../../constants/texts.constants';
import {
	ButtonsContainer,
	ContainerActions,
	HeadContainer,
	NewPaymentLinkButton,
	PLCardIcon,
	PLIconContainer,
	ProductFilterLabel,
	SelectContainer,
	SimpleButton,
	SkeletonSVG,
	TableFilterHeader,
	TitlePage,
} from './styles';

type PaymentLink = 'single' | 'multi';

interface ModalPaymentLink {
	show: boolean;
	type?: PaymentLink;
}

const defaultModalPaymentLink: ModalPaymentLink = {
	show: false,
	type: undefined,
};

const PaymentStatusComponent = ({
	subStatus,
	initexpiredatdate,
	initexpiredattime,
}: {
	subStatus: CustomLinkSubStatus;
	numpayments: number;
	endexpiredatdate: string;
	endexpiredattime: string;
	initexpiredatdate: string;
	initexpiredattime: string;
}) => {
	let message = '';

	if (subStatus === 'active') {
		message = 'Este link está activo, puede compartirse y recibir pagos';
	} else if (subStatus === 'finalized') {
		message = 'Esta link ya no puede reactivarse ni recibir pagos';
	} else if (subStatus === 'completed' || subStatus === 'expired') {
		message =
			'Este link alcanzó su límite de pagos o venció. Puede reactivarse para recibir más pagos';
	} else if (subStatus === 'paused') {
		message = 'Este link fue pausado manualmente. Puede reactivarse para recibir más pagos';
	} else if (subStatus === 'pending') {
		message = `Este link esta pendiente, puede recibir pagos a partir del ${initexpiredatdate} ${initexpiredattime}`;
	}

	return (
		<StatusContainer>
			<Tooltip text={message} position="top">
				<CustomLinkComponentStatus subStatus={subStatus} borderFull={true} />
			</Tooltip>
		</StatusContainer>
	);
};

const defaultFilters: Filters = {
	orderBy: 'asc',
	key: '',
	status: undefined,
	dateRange: undefined,
	typeSelected: undefined,
};

const LinksPage = () => {
	const dictionary = new Map([
		['payment-link-custom', 'Links de pago'],
		['links', 'Mis enlaces'],
	]);

	const [modal, setModal] = useState(defaultModalPaymentLink);
	const [shareModal, setShareModal] = useState<boolean>(false);
	const [editLinkModal, setEditLinkModal] = useState<boolean>(false);
	const [modalLink, setModalLink] = useState<boolean>(false);
	const [modalPayment, setModalPayment] = useState<boolean>(false);
	const [modalPauseLink, setModalPauseLink] = useState<boolean>(false);

	const [rowData, setRowData] = useState<CustomLink>({} as CustomLink);
	const [rowDataPayment, setRowDataPayment] = useState<Payment | null>(null);
	const handleShowModal = (type: PaymentLink) => {
		setModal({ show: true, type });
	};

	const handleCloseModal = () => {
		setModal(defaultModalPaymentLink);
	};

	const handleShowShareModal = (rowData: CustomLink) => {
		setRowData(rowData);
		setShareModal(true);
	};

	const handleCloseShareModal = () => {
		setShareModal(false);
	};

	const handleShowEditLinkModal = (rowData: CustomLink) => {
		setRowData(rowData);
		setEditLinkModal(true);
	};

	const handleCloseEditLinkModal = (refetched?: boolean) => {
		setEditLinkModal(false);
		if (refetched) refetch();
	};

	const handleShowModalLink = (rowData: CustomLink) => {
		setRowData(rowData);
		setModalLink(true);
	};

	const handleCloseModalLink = () => {
		setModalLink(false);
	};

	const handleShowPaymentModal = (rowData: Payment) => {
		setRowDataPayment(rowData);
		setModalPayment(true);
	};

	const handleClosePaymentModal = () => {
		setModalPayment(false);
		setRowDataPayment(null);
	};

	const handleShowPauseLinkModal = (rowData: CustomLink) => {
		setRowData(rowData);
		setModalPauseLink(true);
		handleCloseEditLinkModal(true);
	};

	const handleClosePauseLinkModal = () => {
		setModalPauseLink(false);
	};

	const { merchantSelected } = useMerchant();
	const [pagination, setPagination] = useState(defaultPagination);
	const [filters, setFilters] = useState(defaultFilters);

	const [total, setTotal] = useState(0);
	const {
		data: table,
		isFetching,
		isFetched,
		isLoading: isLoadingTable,
		refetch,
	} = useQuery({
		enabled: !!merchantSelected,
		useErrorBoundary: true,
		keepPreviousData: true,
		queryKey: [
			CONSTANTS.services.paymentLinksCustom.getAllLinks,
			merchantSelected?.code,
			pagination,
			filters,
		],
		queryFn: () =>
			PaymentLinksCustomServices.getAllLinks({
				merchantCode: merchantSelected?.code as string,
				pagination,
				filters: {
					...filters,
					stateSelected: filters?.status,
					dateRange: {
						from: filters?.dateRange?.from,
						to: filters?.dateRange?.to,
					},
				},
			}),
		onSuccess: (data) => {
			if (data) setTotal(data.total);
		},
	});
	const getColumns = () => {
		const columns: ColumnInterface<CustomLink>[] = [
			{
				title: 'Nombre Enlace',
				propertyName: 'name',
				align: 'left',
				cellRender: ({ name }) => <>{name}</>,
			},
			{
				sortField: true,
				title: 'Aperturas',
				align: 'left',
				propertyName: 'openings',
				cellRender: ({ openings }) => <>{openings}</>,
			},
			{
				sortField: true,
				title: 'Pagos recibidos',
				align: 'left',
				propertyName: 'numsuccesspayments',
				cellRender: ({ numsuccesspayments }) => (
					<AmountContainer>{numsuccesspayments}</AmountContainer>
				),
			},
			{
				title: 'Monto',
				align: 'left',
				propertyName: 'amount',
				cellRender: ({ amounttype, amount, orderamountmax, orderamountmin }) => (
					<AmountContainer>
						{amounttype === 'FIXED' ? (
							<>{amount}</>
						) : (
							<>
								{orderamountmin} - {orderamountmax}
							</>
						)}
					</AmountContainer>
				),
			},
			{
				title: 'Estatus',
				propertyName: 'status',
				align: 'left',
				cellRender: ({
					sub_status,
					numpayments,
					endexpiredatdate,
					endexpiredattime,
					initexpiredatdate,
					initexpiredattime,
				}) => {
					return (
						<PaymentStatusComponent
							subStatus={sub_status}
							numpayments={numpayments}
							endexpiredatdate={endexpiredatdate}
							endexpiredattime={endexpiredattime}
							initexpiredatdate={initexpiredatdate}
							initexpiredattime={initexpiredattime}
						/>
					);
				},
			},
			{
				title: 'Tipo de enlace',
				propertyName: 'paymenttype',
				align: 'left',
				cellRender: ({ paymenttype, status }) => {
					return (
						<PLIconContainer disabled={status === 'inactive'}>
							{paymenttype.toLowerCase() === 'unique' ? (
								<UniquePayIcon disabled={status === 'inactive'} />
							) : (
								<RecurrentPayIcon disabled={status === 'inactive'} />
							)}
						</PLIconContainer>
					);
				},
			},
			{
				sortField: true,
				title: 'Fecha de inicio',
				propertyName: 'createdat',
				align: 'left',

				cellRender: ({ createdatdate, createdattime }) => (
					<DateAndTime date={createdatdate} time={createdattime} />
				),
			},
			{
				title: '',
				propertyName: '',
				align: 'left',
				cellRender: (rowData: CustomLink) => (
					<ContainerActionsLinksComponent disabled={false}>
						<ContainerActions>
							{rowData.status === 'active' ? (
								<SimpleButton onClick={() => handleShowShareModal(rowData)} borderColor="#857BF9">
									<CustomShareIcon />
								</SimpleButton>
							) : (
								<SimpleButton disabled={true}>
									<SkeletonSVG />
								</SimpleButton>
							)}
							{rowData.sub_status !== 'finalized' ? (
								<SimpleButton onClick={() => handleShowEditLinkModal(rowData)}>
									<EditIcon />
								</SimpleButton>
							) : (
								<SimpleButton disabled={true}>
									<SkeletonSVG />
								</SimpleButton>
							)}
							<SimpleButton onClick={() => handleShowModalLink(rowData)}>
								<EyeIcon />
							</SimpleButton>
						</ContainerActions>
					</ContainerActionsLinksComponent>
				),
			},
		];

		return columns;
	};

	const renderSelectByType = () => (
		<SelectContainer>
			<Select
				options={[
					{ label: 'Todos', value: '', customLabel: 'Todos', show: true },
					{
						label: (
							<ProductFilterLabel>
								<UniquePayIcon /> Únicos
							</ProductFilterLabel>
						),
						value: 'UNIQUE',
						customLabel: 'Únicos',
						show: true,
					},
					{
						label: (
							<ProductFilterLabel>
								<RecurrentPayIcon /> Recurrentes
							</ProductFilterLabel>
						),
						value: 'RECURRENT',
						customLabel: 'Recurrentes',
						show: true,
					},
				]}
				prefix="Tipo:"
				onChange={({ value }) => {
					setFilters((prevState) => ({ ...prevState, typeSelected: value as string }));
				}}
			/>
		</SelectContainer>
	);

	useEffect(() => {
		setFilters(defaultFilters);
		setPagination(defaultPagination);
	}, [merchantSelected]);

	return (
		<ViewWrapper title="Payment Links" isLoading={isLoadingTable || isFetching}>
			{modal.show && modal.type === 'single' && validatePermission('PL_NEW') && (
				<NewLinkPL show={modal.show} onClose={handleCloseModal} />
			)}
			{editLinkModal && (
				<EditLinkPL
					show={editLinkModal}
					onClose={handleCloseEditLinkModal}
					data={rowData}
					showPauseLinkModal={() => handleShowPauseLinkModal(rowData)}
				/>
			)}
			{shareModal && (
				<ShareLinkModal show={shareModal} onClose={handleCloseShareModal} data={rowData} />
			)}
			{modalLink && (
				<ModalLink
					show={modalLink}
					onClose={handleCloseModalLink}
					data={rowData}
					handleShowModalPayment={handleShowPaymentModal}
					allowClickOutside={false}
				/>
			)}
			{modalPayment && rowDataPayment != null && (
				<ModalPayment
					show={modalPayment}
					onClose={handleClosePaymentModal}
					data={rowDataPayment}
					positionType="top-right"
				/>
			)}

			{modalPauseLink && (
				<PauseLinkModal show={modalPauseLink} onClose={handleClosePauseLinkModal} data={rowData} />
			)}
			<HeadContainer>
				<PLCardIcon selected>
					<TitlePage>
						<PaymentLinksIcon />
					</TitlePage>
				</PLCardIcon>
				<Breadcrumb dictionary={dictionary} />

				<ButtonsContainer>
					<NewPaymentLinkButton onClick={() => handleShowModal('single')}>
						{TEXTS.CTA}
					</NewPaymentLinkButton>
				</ButtonsContainer>
			</HeadContainer>
			<TableFilterHeader>
				<ETPayFilters
					filters={filters}
					onFilterChange={setFilters}
					pagination={pagination}
					setPagination={setPagination}
				/>
			</TableFilterHeader>

			<CustomPaymentLinksTable
				titleTable=" "
				data={table?.tableInfo || []}
				columns={getColumns()}
				customFilter={renderSelectByType()}
				isLoading={isFetching && !isFetched}
				stateList={table?.typeOfStatus || []}
				filters={filters}
				hasFilters={true}
				hasSearch={true}
				onFilterChange={setFilters}
				pagination={pagination}
				setPagination={setPagination}
				totalResults={total}
			/>
		</ViewWrapper>
	);
};

export { LinksPage };
